import React from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Card,
  Icon,
  StackView,
} from '@planningcenter/tapestry-react'
import {
  Code,
  Link as SharedLink,
  List,
  PageHeading,
  Heading,
  Text,
} from '../../../shared/typography'
import Image from '../../../shared/image'
import initially_unauthorized_add_on from '../../../../assets/images/initially_unauthorized_add_on.png'
import planning_center_authorize from '../../../../assets/images/planning_center_authorize.png'
import integrator_authorize from '../../../../assets/images/integrator_authorize.png'
import add_on_authorization_flow_chart from '../../../../assets/images/add_on_authorization_flow_chart.png'

const page = () => {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>Add-ons and OAuth</PageHeading>

      <Text>
        Your add-on will need to make authenticated requests to both Planning
        Center's API and your application's API. To do so, we make use of OAuth
        2.0's "Authorization Code" Flow.
      </Text>

      <Text>
        At a minimum, there will be two OAuth Providers: Planning Center and
        your application, as well as two OAuth applications: one you register
        with us{' '}
        <Link
          to="https://api.planningcenteronline.com/oauth/applications"
          external
        >
          here
        </Link>{' '}
        to make requests to Planning Center, and one you create in your
        application and configure for Planning Center in your{' '}
        <Link to="/overview/add-ons/add-ons-cli#config">
          add-on config file
        </Link>{' '}
        to make requests to your API.
      </Text>

      <Text>
        Once configured, we perform the OAuth flow for both providers (Planning
        Center and you) when your add-on is loaded. The end result is your
        add-on's JavaScript code will have access to an{' '}
        <Code>authenticatedFetch</Code> function for making requests to Planning
        Center's API, and an <Code>authenticatedIntegratorFetch</Code> function
        for making requests to your API. More details on those functions can be
        found <Link to="/overview/add-ons/api-reference">here</Link>.
      </Text>

      <Heading anchored id="oauth-handshake" size={1} level={2}>
        Two-way OAuth Handshake
      </Heading>

      <Alert
        stroke="primary-darkest"
        backgroundColor="primary-lightest"
        padding={2}
      >
        <StackView axis="horizontal" alignment="center" spacing={2}>
          <Icon
            name="general.outlinedInfoCircle"
            color="primary-darkest"
            size="xl"
          />
          <Text>
            Fully detailing OAuth is beyond the scope of this documentation. If
            you're looking for those details,{' '}
            <Link
              to="https://oauth.net/2/grant-types/authorization-code/"
              external
            >
              here's
            </Link>{' '}
            a place to start. It's worth noting, as far as add-ons are concerned
            we only use the "authorization code" flow. Also, most frameworks
            have libraries that you can leverage for most (if not all!) of the
            heavy lifting.
          </Text>
        </StackView>
      </Alert>

      <Text>
        Since, add-ons must communicate with both Planning Center's API and your
        API, we need a two-way OAuth handshake. For the purpose of this
        documentation, let's suppose your application is running at{' '}
        <Code>https://example.com</Code> and Planning Center is of course
        running at <Code>https://api.planningcenteronline.com</Code>. Let's
        further suppose your app has the following endpoints:
      </Text>

      <List>
        <List.Item>
          <Text>
            <Code>GET https://example.com/oauth/pco/complete</Code>
          </Text>
          <List>
            <List.Item>
              <Text>
                Once the user has allowed your app to access Planning Center's
                API, we redirect the user here with an{' '}
                <Code>authorization_code</Code>. Your server can exchange that{' '}
                <Code>authorization_code</Code> for an <Code>access_token</Code>
                by POSTing to{' '}
                <Code>https://api.planningcenter.com/oauth/token</Code> with
                that code and your client credentials. Once, you're done, you{' '}
                <b>must</b> redirect to{' '}
                <Code>
                  https://api.planningcenteronline.com/addons/authorizations/:oauth_application_id/advance
                </Code>
                .
              </Text>
            </List.Item>

            <List.Item style={{ listStyleType: 'none' }}>
              <Callout>
                This is worth calling attention to as a non-standard part of the
                OAuth flow, but it's essential to the two-way OAuth handshake we
                use for add-ons. You don't have to use this exact url -- it's
                configurable in your{' '}
                <Link to="/overview/add-ons/add-ons-cli">
                  <Code>config.yaml</Code>
                </Link>{' '}
                file. However, you do <b>have to</b> redirect to Planning Center
                at that exact <Code>/advance</Code> url so we can begin the
                second leg of the two-way handshake. That's where Planning
                Center will request an OAuth token from you.
              </Callout>
            </List.Item>
          </List>
        </List.Item>

        <List.Item>
          <Text>
            <Code>GET https://example.com/oauth/authorize</Code>
          </Text>
          <List>
            <List.Item>
              <Text>
                {' '}
                This is the page that prompts the user to allow Planning Center
                to access your API. If the user is not already logged into your
                system, you should do that before showing them this prompt.
                Generally, this prompt is a form that POSTs back to the same
                endpoint.
              </Text>
            </List.Item>
          </List>
        </List.Item>

        <List.Item>
          <Text>
            <Code>POST https://example.com/oauth/authorize</Code>
          </Text>
          <List>
            <List.Item>
              <Text>
                Assuming the user allowed Planning Center to access, this
                endpoint creates an <Code>authorization_code</Code> and
                redirects back to Planning Center with that{' '}
                <Code>authorization_code</Code>.
              </Text>
            </List.Item>
          </List>
        </List.Item>

        <List.Item>
          <Text>
            <Code>POST https://example.com/oauth/token</Code>
          </Text>
          <List>
            <List.Item>
              <Text>
                This is the endpoint that receives the POST request to convert
                the <Code>authorization_code</Code> into an{' '}
                <Code>access_token</Code>. We will also use this endpoint to
                refresh a token if the token is about to expire. (Token
                expiration is optional and up to you. If there is an expiration
                value present, then we will refresh the token a few minutes
                before it expires.)
              </Text>
            </List.Item>
          </List>
        </List.Item>
      </List>

      <Heading anchored id="oauth-handshake-step-0" level={3} size={2}>
        Step 0: Clicking the Authorize Button
      </Heading>

      <Text>
        When your add-on is first initialized, the user is presented with an
        "Authorize" button. Clicking it begins the two-way OAuth handshake.
      </Text>

      <Box>
        <Image src={initially_unauthorized_add_on} style={{ width: '768px' }} />
      </Box>

      <Heading anchored id="oauth-handshake-step-1" level={3} size={2}>
        Step 1: Redirect to{' '}
        <Code>https://api.planningcenteronline.com/oauth/authorize</Code>
      </Heading>

      <Text>
        First, you get a token to Planning Center. The user is presented with an
        authorization prompt like this:
      </Text>

      <Box>
        <Image src={planning_center_authorize} style={{ width: '768px' }} />
      </Box>

      <Heading anchored id="oauth-handshake-step-2" level={3} size={2}>
        Step 2: Redirect to <Code>https://example.com/oauth/pco/complete</Code>
      </Heading>

      <Callout>
        This is configurable per <Code>environment</Code> via the{' '}
        <Code>redirect_url</Code> in your{' '}
        <Link to="/overview/add-ons/add-ons-cli">
          <Code>config.yaml</Code>
        </Link>{' '}
        file.
      </Callout>

      <Text>
        Once the user allows your app to access Planning Center, they're
        redirected here. This redirect will include an{' '}
        <Code>authorization_code</Code>. As stated, your server can exchange
        that <Code>authorization_code</Code> for an <Code>access_token</Code> to
        Planning Center's API, but you don't have to. However, once you're done,
        you <b>must</b> redirect to{' '}
        <Code>
          https://api.planningcenteronline.com/addons/authorizations/:oauth_application_id/advance
        </Code>
      </Text>

      <Text>
        The <Code>:oauth_application_id</Code> is an integer ID that represents
        your application in our system. This is not the <Code>client_id</Code>.
        You can find it by going to{' '}
        <Link
          to="https://api.planningcenteronline.com/oauth/applications"
          external
        >
          https://api.planningcenteronline.com/oauth/applications
        </Link>
        , clicking edit, and noting the ID from the URL. For example, if the URL
        is{' '}
        <Code>
          https://api.planningcenteronline.com/oauth/applications/42/edit
        </Code>
        , then your
        <Code>oauth_application_id</Code> is 42.
      </Text>

      <Heading anchored id="oauth-handshake-step-3" level={3} size={2}>
        Step 3: Redirect to{' '}
        <Code>
          https://api.planningcenteronline.com/addons/authorizations/:oauth_application_id/advance
        </Code>
      </Heading>

      <Text>
        At this point, both you and Planning Center have an{' '}
        <Code>access_token</Code> that can be used to make requests to Planning
        Center's API. Now, we need to do the same for your API so the add-on can
        make requests to it. Just like the user had to allow you to access
        Planning Center's API, they now are given an opportunity to allow
        Planning Center to access your API.
      </Text>

      <Heading anchored id="oauth-handshake-step-4" level={3} size={2}>
        Step 4: Redirect to <Code>https://example.com/oauth/authorize</Code>
      </Heading>

      <Callout>
        This is configurable per <Code>environment</Code> via the{' '}
        <Code>authorize_url</Code> in your{' '}
        <Link to="/overview/add-ons/add-ons-cli">
          <Code>config.yaml</Code>
        </Link>{' '}
        file.
      </Callout>

      <Text>
        Much like Step 1, the user is presented with a prompt. What that looks
        like is up to you, but as an example it could look something like this:
      </Text>

      <Box>
        <Image src={integrator_authorize} style={{ width: '768px' }} />
      </Box>

      <Text>
        That prompt will POST back to{' '}
        <Code>http://example.com/oauth/authorize</Code> where your server will
        generate an <Code>authorization_code</Code> and send it to Planning
        Center by redirecting the user.
      </Text>

      <Heading anchored id="oauth-handshake-step-5" level={3} size={2}>
        Step 5: Redirect to{' '}
        <Code>
          https://api.planningcenteronline.com/addons/authorizations/:oauth_application_id
        </Code>
      </Heading>

      <Text>
        Planning Center will POST that <Code>authorization_code</Code> and the
        client credentials you configure in your add-ons{' '}
        <Code>config.yaml</Code>, to exchange them for an{' '}
        <Code>access_token</Code> that can be used to make requests to your API.
      </Text>

      <Heading anchored id="oauth-handshake-step-6" level={3} size={2}>
        Step 6: POST to <Code>https://example.com/oauth/token</Code>
      </Heading>

      <Callout>
        This is configurable per <Code>environment</Code> via the{' '}
        <Code>token_url</Code> in your{' '}
        <Link to="/overview/add-ons/add-ons-cli">
          <Code>config.yaml</Code>
        </Link>{' '}
        file.
      </Callout>

      <Text>
        Your server validates the <Code>authorization_code</Code> and the client
        credentials, and issues an <Code>access_token</Code>.
      </Text>

      <Text>
        That concludes the two-way OAuth handshake. Planning Center will close
        the window it used for redirecting and prompting the user, and your
        add-on will be ready for use!
      </Text>

      <Heading anchored id="best-practices" size={1} level={2}>
        Best Practices
      </Heading>

      <Heading anchored id="best-practice-use-a-library" level={3} size={2}>
        Use a Library
      </Heading>

      <Text>
        There's little reason to implement the OAuth protocol yourself. You'll
        generally need to implement the OAuth protocol both from the perspective
        of an "OAuth Provider" (to issue <Code>access_tokens</Code> to Planning
        Center) and an "OAuth Client" (to request an <Code>access_token</Code>{' '}
        from Planning Center). Most web frameworks will have well-established
        libraries for both pieces. For example, in Rails we have{' '}
        <Link to="https://github.com/doorkeeper-gem/doorkeeper" external>
          doorkeeper
        </Link>{' '}
        for the OAuth provider piece, and the{' '}
        <Link to="https://gitlab.com/oauth-xx/oauth2/" external>
          oauth2
        </Link>{' '}
        library for the OAuth Client piece.
      </Text>

      <Heading anchored id="separate-apps" level={3} size={2}>
        Create a Separate OAuth Application for Your Add-on
      </Heading>

      <Text>
        Many add-on authors already have an existing integration with Planning
        Center as a part of their offering.{' '}
        <b>Don't reuse that for your add-on!</b> Even if you have an existing
        OAuth application, we recommend you create a new one just for your
        add-on so tokens and logs are kept separate.
      </Text>

      <Heading anchored id="dont-save-access-tokens" level={3} size={2}>
        Don't Save <Code>access_tokens</Code> You Don't Need To
      </Heading>

      <Text>
        In Step 2 when we redirect to{' '}
        <Code>https://example.com/oauth/pco/complete</Code>, we'll include an{' '}
        <Code>authorization_code</Code> in that redirect you can exchange for an{' '}
        <Code>access_token</Code>, but you might not have to.
      </Text>

      <Text>
        {' '}
        Even if you don't, we'll make an <Code>access_token</Code> available to
        your the frontend code of your add-on via the{' '}
        <Code>authenticatedFetch</Code> function.
      </Text>

      <Text>
        You probably don't need to if you have an existing OAuth application.
        Your server can continue to levearge that existing integration. This is
        ideal because then the requests your add-on's UI makes to Planning
        Center are grouped separately from the requests your server makes to
        Planning Center in our logs, which can aid in troubleshooting.
      </Text>

      <Text>
        You might not need to if your feature doesn't need Planning Center's
        data. For example, if you're building a texting service and all you need
        is a phone number, your add-on's frontend can fetch that phone number
        using <Code>authenticatedFetch</Code>, and pass it to your API via{' '}
        <Code>authenticatedIntegratorFetch</Code>.
      </Text>

      <Heading anchored id="save-access-tokens-securely" level={3} size={2}>
        Store Tokens Securely
      </Heading>

      <Text>
        When you do need to store <Code>access_tokens</Code> and{' '}
        <Code>refresh_tokens</Code>, you must do so securely. Remember, these
        all have privileged access to our shared customer's data inside Planning
        Center.
      </Text>

      <List>
        <List.Item>
          <Text>
            Never store the <Code>client_secret</Code>,{' '}
            <Code>access_token</Code> or <Code>refresh_token</Code> in a mobile
            application or on any public client.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <Code>access_tokens</Code> and <Code>refresh_tokens</Code> can be
            stored in a secure database. When they are, they should be encrypted
            using a strong encryption standard such as AES. Also, avoid storing
            the <Code>client_secret</Code> in the same database as the{' '}
            <Code>refresh_token</Code>, because compromising both allows an
            attacker to request new <Code>access_tokens</Code>.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Never put your <Code>client_secret</Code> in source code. Instead,
            use an environment variable to reference the secret in your
            application.
          </Text>
        </List.Item>
      </List>

      <Heading anchored id="authorization-flow" size={1} level={2}>
        Authorization Flow
      </Heading>

      <Text>
        This is the general flow of activating and authorizing an add-on from
        with in Planning Center as a flow chart. It includes the two-way OAuth
        handshake, as well as what needs to happen before and after:
      </Text>

      <Box>
        <Image src={add_on_authorization_flow_chart} />
      </Box>
    </StackView>
  )
}

export default page

function Link({ external, ...props }) {
  return external ? (
    <SharedLink external {...props} />
  ) : (
    <ReactRouterLink component={SharedLink} {...props} />
  )
}

function Callout({ children }) {
  return (
    <Card
      axis="horizontal"
      alignment="center"
      padding={1}
      color="grey-8"
      subdued
    >
      <Text italic size={4}>
        {children}
      </Text>
    </Card>
  )
}
